import React, { useState, useEffect } from 'react';
import { X, Phone, Mail, MapPin, Calendar, User, Building, MessageSquare, Tag, UserCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useLeads, Lead } from '../hooks/useLeads';

interface LeadDetailModalProps {
  leadId: string;
  isOpen: boolean;
  onClose: () => void;
}

const statusConfig = {
  new: { label: 'Novo', color: 'bg-blue-100 text-blue-800' },
  contacted: { label: 'Contatado', color: 'bg-yellow-100 text-yellow-800' },
  qualified: { label: 'Qualificado', color: 'bg-purple-100 text-purple-800' },
  converted: { label: 'Convertido', color: 'bg-green-100 text-green-800' },
  lost: { label: 'Perdido', color: 'bg-red-100 text-red-800' },
  archived: { label: 'Arquivado', color: 'bg-gray-100 text-gray-800' },
  perdido: { label: 'Perdido', color: 'bg-red-100 text-red-800' }
};

const sourceConfig = {
  website: { label: 'Site', icon: '🌐' },
  whatsapp: { label: 'WhatsApp', icon: '💬' },
  olx: { label: 'OLX', icon: '🏷️' },
  facebook: { label: 'Facebook', icon: '📘' },
  instagram: { label: 'Instagram', icon: '📷' },
  phone: { label: 'Telefone', icon: '📞' },
  manual: { label: 'Manual', icon: '✏️' }
};

export const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  leadId,
  isOpen,
  onClose
}) => {
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const { getLeadById, updateLeadStatus, convertLeadToClient } = useLeads();

  useEffect(() => {
    if (isOpen && leadId) {
      loadLead();
    }
  }, [isOpen, leadId]);

  const loadLead = async () => {
    setLoading(true);
    const leadData = await getLeadById(leadId);
    setLead(leadData);
    setLoading(false);
  };

  const handleStatusChange = async (newStatus: Lead['status']) => {
    if (!lead) return;
    
    await updateLeadStatus(lead.id, newStatus);
    setLead((prev: Lead | null) => prev ? { ...prev, status: newStatus } : null);
  };

  const handleConvertToClient = async () => {
    if (!lead) return;
    
    const success = await convertLeadToClient(lead.id);
    if (success) {
      setLead((prev: Lead | null) => prev ? { ...prev, status: 'converted' } : null);
      onClose();
    }
  };

  const handleCallAndConvert = async () => {
    if (!lead?.phone) return;
    
    // Abrir o telefone
    window.open(`tel:${lead.phone}`);
    
    // Converter automaticamente para cliente
    const success = await convertLeadToClient(lead.id);
    if (success) {
      setLead((prev: Lead | null) => prev ? { ...prev, status: 'converted' } : null);
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Detalhes do Lead</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando...</p>
          </div>
        ) : !lead ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">Lead não encontrado</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Header do Lead */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {lead.name}
                  </h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(lead.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Tag className="h-4 w-4" />
                      {(sourceConfig as any)[lead.source]?.icon} 
                      {(sourceConfig as any)[lead.source]?.label || lead.source}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[lead.status].color}`}>
                    {statusConfig[lead.status].label}
                  </span>
                  <select
                    value={lead.status}
                    onChange={(e) => handleStatusChange(e.target.value as Lead['status'])}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Informações de Contato */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-800">Informações de Contato</h4>
                <div className="space-y-3">
                  {lead.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{lead.phone}</p>
                        <a 
                          href={`tel:${lead.phone}`}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Ligar agora
                        </a>
                      </div>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{lead.email}</p>
                        <a 
                          href={`mailto:${lead.email}`}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Enviar email
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mensagem do Lead */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Mensagem
                  </h5>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700">{lead.message}</p>
                  </div>
                </div>

                {/* Corretor Responsável */}
                {lead.assigned_user && (
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Corretor Responsável</h5>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-medium">{lead.assigned_user.first_name} {lead.assigned_user.last_name}</p>
                      <p className="text-sm text-gray-600">{lead.assigned_user.email}</p>
                      {lead.assigned_user.phone && (
                        <p className="text-sm text-gray-600">{lead.assigned_user.phone}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Informações do Imóvel */}
              {lead.property && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Imóvel de Interesse
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div>
                      <p className="font-medium text-gray-800">{lead.property.title}</p>
                      <p className="text-sm text-gray-600">Código: {lead.property.listing_id}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-500 mt-0.5" />
                      <p className="text-sm text-gray-600">{lead.property.address}</p>
                    </div>
                    {lead.property.list_price && (
                      <p className="text-lg font-semibold text-green-600">
                        {formatPrice(lead.property.list_price)}
                      </p>
                    )}
                    {(lead.property.bedrooms || lead.property.bathrooms) && (
                      <div className="flex gap-4 text-sm text-gray-600">
                        {lead.property.bedrooms && (
                          <span>{lead.property.bedrooms} quartos</span>
                        )}
                        {lead.property.bathrooms && (
                          <span>{lead.property.bathrooms} banheiros</span>
                        )}
                      </div>
                    )}
                    {lead.property.images && lead.property.images.length > 0 && (
                      <div className="mt-3">
                        <img
                          src={lead.property.images[0]}
                          alt={lead.property.title}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="border-t pt-6">
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={onClose}>
                  Fechar
                </Button>
                {lead.status !== 'converted' && (
                  <Button 
                    variant="outline"
                    className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
                    onClick={handleConvertToClient}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Converter em cliente
                  </Button>
                )}
                {lead.phone && (
                  <Button 
                    onClick={lead.status === 'converted' ? () => window.open(`tel:${lead.phone}`) : handleCallAndConvert}
                    className={lead.status === 'converted' ? '' : 'bg-green-600 hover:bg-green-700'}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {lead.status === 'converted' ? 'Ligar' : 'Ligar e converter'}
                  </Button>
                )}
                {lead.email && (
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`mailto:${lead.email}`)}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};